import React, { type ReactElement, useState } from 'react';
import { InterPoint } from '@saviynt/common';
import {
  Box,
  ButtonIcon,
  Chip,
  Icon,
  ListRow,
  ListRowGroup,
  Menu,
  Typography,
} from '@saviynt/design-system';

import {
  CF_NEU_BLD,
  CF_NEU_MED,
  CF_NEU_STL,
  CF_SEC_MED,
  ENTITLEMENT_DEATILS_TABS,
  violationsEntitlement,
} from '../../../../../../shared/constants';
import { useInvertedLocalize } from '../../../../../../shared/hooks';
import { type EntitlementRisksRowType } from '../data-layer/types';

import './InherentRisksListGroup.css';

// eslint-disable-next-line no-warning-comments
// TODO: Localize Menu Labels

interface InherentRisksListGroupProps {
  handleOpenSidePanel: (item: EntitlementRisksRowType) => void;
  selectedRow: EntitlementRisksRowType | null;
  listRows: EntitlementRisksRowType[];
  isSidePanelOpen: boolean;
  iconType?: string;
  handleSourceChange: (sourceType: string, activeTab: string) => void;
  initialOptionsWithIcons: { label: string; value: string; icon: string }[];
}

/**
 * * TODO
 * - Write a description
 * - Localize Menu Option Labels
 */
function InherentRisksListGroup({
  handleOpenSidePanel,
  selectedRow,
  listRows,
  isSidePanelOpen,
  iconType,
  handleSourceChange,
  initialOptionsWithIcons,
}: InherentRisksListGroupProps): ReactElement | null {
  const [expandedMenuId, setExpandedMenuId] = useState<number | null>(null);
  const l = useInvertedLocalize();

  const getCenterContent = (item: EntitlementRisksRowType) => {
    const { title, description, critical } = item;

    return (
      <Box className='InherentRisksListGroup-centerContent'>
        <Typography kind='body3' color={CF_SEC_MED}>
          {l('Risk')}
        </Typography>
        <Typography kind='h3' color={CF_NEU_BLD}>
          {title}
        </Typography>
        <Box className='InherentRisksListGroup-rowDetails'>
          <Chip
            size='xSmall'
            kind='subtleFilled'
            label={`Critical (${critical ?? 0})`}
            color='pink'
          />
          <InterPoint color='neutralBold' font='body2' />
          <Typography kind='body2' color={CF_NEU_STL}>
            {l('Functions:')}
          </Typography>
          <Typography kind='body2' color={CF_NEU_BLD} lineLimit={1}>
            {description}
          </Typography>
        </Box>
      </Box>
    );
  };

  const getTrailingContent = (item: EntitlementRisksRowType) => {
    const isExpanded = expandedMenuId === item.id;

    const menuTrigger = (
      <ButtonIcon
        size='medium'
        kind='ghost'
        isSelected={isExpanded}
        icon={<Icon kind='MoreVertical' />}
      />
    );

    const handleMenuExpand = (isMenuOpen: boolean) => {
      if (isMenuOpen) {
        setExpandedMenuId(null);
      } else {
        setExpandedMenuId(item.id);
      }
    };

    return (
      <Box className='InherentRisksListGroup-trailingContent'>
        <Box className='InherentRisksListGroup-rowLinkBlocks'>
          <Box className='InherentRisksListGroup-rowLinkBlock'>
            <Icon
              kind='ArrowRight'
              size='large'
              color={CF_NEU_BLD}
              className='InherentRisksListGroup-rowLinkBlockIcon'
            />
          </Box>
        </Box>
        {/* Placeholder Button */}
        <Box className='InherentRisksListGroup-rowKabobWrapper'>
          <Menu
            options={initialOptionsWithIcons}
            onChange={() => {
              handleSourceChange(
                violationsEntitlement,
                ENTITLEMENT_DEATILS_TABS.TAB_VIOLATIONS
              );
            }}
            minOptionsForSearch={10}
            trigger={menuTrigger}
            setIsExpanded={(isMenuOpen: boolean) => {
              handleMenuExpand(isMenuOpen);
            }}
            placement='bottom-end'
            isPortalEnabled
          />
        </Box>
      </Box>
    );
  };

  if (listRows.length === 0) {
    return (
      <Box className='InherentRisksListGroup-emptyList'>
        <Typography kind='body1' color={CF_NEU_MED}>
          {l('No records match your search')}
        </Typography>
      </Box>
    );
  }

  return (
    <ListRowGroup className='InherentRisksListGroup'>
      {listRows.map((item) => (
        <ListRow
          key={item.id}
          centerContent={getCenterContent(item)}
          leadingComp={
            <Icon
              kind={iconType ?? 'Entitlement'}
              color={CF_SEC_MED}
              size='xLarge'
              className='InherentRisksListGroup-iconType'
            />
          }
          trailingContent={!isSidePanelOpen && getTrailingContent(item)}
          isCheckboxDisabled
          handleRowClick={() => handleOpenSidePanel(item)}
          isRowDetailsOpen={selectedRow?.id === item.id}
          connectorDistance='1.625rem'
        />
      ))}
    </ListRowGroup>
  );
}

InherentRisksListGroup.defaultProps = {
  iconType: '',
};

export default InherentRisksListGroup;
